import {
  inviteOwnerToPortal,
  removeOwnerPortalAccess,
  useCreateOwner,
  useDeleteOwner,
  useListOwners,
  useUpdateOwner,
  type OwnerIn,
  type OwnerListOut,
  type OwnerOut,
} from '@myrentalmate/api-client';

import { Link as MuiLink, Stack } from '@mui/material';
import { Link } from 'react-router';

import {
  EMAIL_FIELD,
  optional,
  PHONE_FIELD,
  phoneColumn,
  emailColumn,
  RecordPage,
  required,
  type Column,
  type Field,
  type FormValues,
  type ListParams,
} from '../../components/RecordPage';
import { StatRow, StatTile } from '../../components/Stats';
import { listResult, okData } from '../../lib/apiData';
import { formatPhone } from '../../lib/contact';
import { useCan } from '../../lib/permissions';
import { useDashboardSummary } from '../../lib/summary';
import { PortalAccess } from '../records/PortalAccess';
import { RecordExtras } from '../records/RecordExtras';

const FIELDS: Field[] = [
  { name: 'name', label: 'Name', required: true, helperText: 'A person or a company' },
  EMAIL_FIELD,
  PHONE_FIELD,
  { name: 'mailing_address', label: 'Mailing Address', type: 'multiline' },
  { name: 'notes', label: 'Notes', type: 'multiline' },
];

const COLUMNS: Column<OwnerListOut>[] = [
  { label: 'Name', render: (o) => o.name },
  emailColumn(),
  phoneColumn(),
  {
    label: 'Properties',
    render: (o) =>
      o.properties.length === 0 ? (
        '–'
      ) : (
        <Stack>
          {o.properties.map((p) => (
            <MuiLink key={p.property_id} component={Link} to={`/staff/properties/${p.property_id}`}>
              {p.property_name} ({Number(p.ownership_percent)}%)
            </MuiLink>
          ))}
        </Stack>
      ),
  },
];

function toBody(values: FormValues): OwnerIn {
  return {
    name: required(values, 'name'),
    email: optional(values, 'email'),
    phone: optional(values, 'phone'),
    mailing_address: optional(values, 'mailing_address'),
    notes: optional(values, 'notes'),
  };
}

function toValues(o: OwnerOut): FormValues {
  return {
    name: o.name,
    email: o.email ?? '',
    phone: formatPhone(o.phone),
    mailing_address: o.mailing_address ?? '',
    notes: o.notes ?? '',
  };
}

function useRows({ q, page, pageSize }: ListParams) {
  return listResult(useListOwners({ q, page, page_size: pageSize }));
}

export function OwnersPage() {
  const canManage = useCan('owners.manage');
  const create = useCreateOwner();
  const update = useUpdateOwner();
  const remove = useDeleteOwner();
  const summary = useDashboardSummary();
  return (
    <RecordPage
      title="Owners"
      description="The people and companies you manage for. Set who owns what on each property's page."
      summary={
        <StatRow>
          <StatTile label="Owners" value={summary?.owners} />
          <StatTile label="Properties" value={summary?.properties} />
          <StatTile label="Properties with No Owner" value={summary?.properties_without_owner} />
        </StatRow>
      }
      singular="owner"
      fields={FIELDS}
      columns={COLUMNS}
      canManage={canManage}
      useRows={useRows}
      toValues={toValues}
      create={(values) => create.mutateAsync({ data: toBody(values) })}
      update={(recordId, values) => update.mutateAsync({ recordId, data: toBody(values) })}
      remove={(recordId) => remove.mutateAsync({ recordId })}
      renderExtras={(row) => (
        <>
          {canManage && (
            <PortalAccess
              title="Owner Portal"
              joinedText="They can sign in to see their properties, statements and distributions."
              inviteText="Invite them to see their properties, statements and distributions. The invitation goes to the email above."
              joined={row.user_id !== null}
              invite={async () => okData(await inviteOwnerToPortal(row.id))?.invite_url}
              remove={() => removeOwnerPortalAccess(row.id)}
            />
          )}
          <RecordExtras entityType="owner" entityId={row.id} canManage={canManage} />
        </>
      )}
    />
  );
}
